import { z } from 'zod'

/**
 * The realtime wire format.
 *
 * Change notifications carry identifiers only — table, operation, id. The
 * client refetches through the normal authorised read path, so a broadcast
 * can never hand someone a row they are not allowed to see, and the payload
 * stays far below Postgres's 8000-byte NOTIFY limit no matter how large the
 * row is.
 */

export const changeEventSchema = z.object({
  type: z.literal('change'),
  table: z.string(),
  op: z.enum(['INSERT', 'UPDATE', 'DELETE']),
  id: z.string(),
  /** Seconds since epoch, from the database clock. */
  at: z.number(),
})
export type ChangeEvent = z.infer<typeof changeEventSchema>

export const serverMessageSchema = z.discriminatedUnion('type', [
  changeEventSchema,
  z.object({ type: z.literal('ready'), userId: z.string() }),
  z.object({ type: z.literal('pong') }),
  z.object({ type: z.literal('error'), message: z.string() }),
])
export type ServerMessage = z.infer<typeof serverMessageSchema>

export const clientMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ping') }),
])
export type ClientMessage = z.infer<typeof clientMessageSchema>

/**
 * Close code for a socket the server would not authenticate.
 *
 * Shared because it is the one close the client must treat differently, and
 * a number that only matched by coincidence would reintroduce the reconnect
 * loop it exists to stop. 4401 is in the 4000–4999 application range, and
 * echoes HTTP 401.
 */
export const UNAUTHORISED_CLOSE = 4401

/**
 * Which parts of the API a changed table invalidates, as tRPC router paths.
 *
 * The shape matters and is easy to get wrong. `@trpc/tanstack-react-query`
 * builds a query key as `[['jobs', 'list'], { input, type }]` — the first
 * element is an *array* of path segments, not a string. A filter of
 * `['jobs']` therefore matches nothing at all, and the failure is silent:
 * the socket connects, events arrive, and no screen ever updates.
 *
 * So these are path segment arrays, and `queryKeysForTable` wraps each one
 * to produce a filter TanStack will actually match. There is a test that
 * asserts the match against a genuine tRPC-generated key, because reading
 * this code cannot tell you whether it is right.
 */
const ROUTER_PATHS_BY_TABLE: Record<string, string[][]> = {
  // Work. A technician touching any of these on site has to reach the office
  // screen without anyone pressing refresh — which is the whole point of the
  // live layer.
  // A job completing moves the next due date of a schedule counted from
  // completion, and a booking turns a created visit into one on the schedule.
  Job: [['jobs'], ['schedule'], ['repeat'], ['agreements']],
  ScheduledVisit: [['schedule'], ['jobs'], ['repeat'], ['agreements']],
  VisitAssignment: [['schedule']],
  StaffLeave: [['schedule']],
  CalendarEvent: [['schedule']],
  CalendarEventAttendee: [['schedule']],
  TimeEntry: [['jobs']],
  Note: [['jobs']],
  Task: [['jobs']],
  JobService: [['jobs']],
  Checklist: [['jobs']],
  ChecklistItem: [['jobs']],
  Attachment: [['jobs']],
  FormResponse: [['jobs']],
  JobContact: [['jobs']],

  // Repeat schedules and agreements. The worker creating a job is nobody's
  // click, so the Schedule tab has to hear about it. Decision 96.
  ServiceAgreement: [['agreements'], ['repeat']],
  RepeatSchedule: [['repeat'], ['agreements'], ['jobs']],
  RepeatScheduleService: [['repeat'], ['agreements']],
  RepeatVisit: [['repeat'], ['agreements']],

  // Customers.
  Client: [['clients'], ['jobs']],
  Site: [['clients'], ['jobs']],
  Contact: [['clients']],

  // Money.
  Quote: [['quotes'], ['jobs']],
  // A customer signing a proposal, or opening its link, is the one change to
  // a quote that nobody in the office made — so it has to arrive by itself.
  QuoteAcceptance: [['quotes'], ['jobs']],
  QuoteShareLink: [['quotes'], ['jobs']],
  Invoice: [['invoices'], ['jobs']],
  // The customer opening an invoice's link. Decision 88.
  InvoiceShareLink: [['invoices'], ['jobs']],
  Payment: [['invoices'], ['payments']],

  // Communication.
  // A delivered, opened or bounced email is written by the provider's
  // webhook, and the send history on a quote or invoice reads it.
  OutboundMessage: [['quotes'], ['invoices'], ['jobs'], ['inbox']],
  InboxMessage: [['inbox']],
  Call: [['inbox']],
  Notification: [['inbox']],
  AssistantToolCall: [['inbox']],
  // A Send later going out, or failing, is the worker's doing. Decision 95.
  ScheduledSend: [['compose'], ['jobs']],

  // People.
  User: [['users'], ['schedule']],
  ScheduleGroup: [['users'], ['schedule']],

  // Dispatch. A bucket holds jobs, so both the board and the Jobs list move
  // when one is parked or picked up.
  DispatchBucket: [['schedule']],
}

/**
 * Query filters for a changed table.
 *
 * Each entry is wrapped in an extra array so the result is
 * `[['jobs']]`, which partially matches `[['jobs', 'list'], { … }]` and so
 * invalidates every query in that router.
 *
 * An unmapped table returns nothing rather than everything. Over-invalidating
 * on a table nobody is watching is wasted requests on every client at once;
 * the tables that matter are listed above and covered by a test that fails if
 * a NOTIFY trigger exists without a mapping.
 */
export function queryKeysForTable(table: string): unknown[][] {
  return (ROUTER_PATHS_BY_TABLE[table] ?? []).map((path) => [path])
}

/** The tables the client expects to hear about. Used to check the triggers. */
export function watchedTables(): string[] {
  return Object.keys(ROUTER_PATHS_BY_TABLE).sort()
}
